import React, {
  useCallback, useEffect, useRef, useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withI18n } from '@active/react-utils/i18n/LocaleContext';
import Checkbox from '@active/react-ui/Checkbox';
import CheckboxGroup from '@active/react-ui/CheckboxGroup';
import FormGroup from '@active/react-ui/FormGroup';
import FormField from '@active/react-ui/FormField';
import CurrencyInput from '@active/react-ui/CurrencyInput';
import isEmpty from 'lodash/isEmpty';
import PaymentOptionGroups from './components/PaymentOptionGroups';
import { interpolate } from '../../../../../../../../../../../shared/utils/commonUtils';
import { currency } from '../../../../../../../../../Currency';
import I18nMessage from '../../../../../../../../../messages/I18nMessage';
import { setAmountDue, setUseCreditBalance, setUpdateBillingSchedule } from '../../../../../../paymentOptionsSlice';

export const PaymentOptionContent = ({
  maxAmount, i18n, currencyCode, locale,
  paymentOptionType, groups = [],
  subOptions = [],
  amount, balance,
}) => {
  const contentRef = useRef(null);
  const dispatch = useDispatch();
  const {
    payLaterDescription,
  } = useSelector(state => state.propsData);

  const handleApplyCreditBalanceChange = useCallback((value) => {
    dispatch(setUseCreditBalance(!isEmpty(value)));
  }, [ dispatch ]);

  const handleUpdateBillingScheduleChange = useCallback((value) => {
    dispatch(setUpdateBillingSchedule(!isEmpty(value)));
  }, [ dispatch ]);

  const customAmountValidate = useCallback((value) => {
    if (value <= 0 || value >= maxAmount) {
      return i18n('checkout.lib.components.error.validate.customAmount');
    }
    return '';
  }, [ maxAmount, i18n ]);

  const handleCustomAmountBlur = useCallback((e) => {
    const inputAmount = e.target.value;
    if (!customAmountValidate(inputAmount)) {
      dispatch(setAmountDue(Number(inputAmount)));
    }
  }, [ customAmountValidate, dispatch ]);

  const isShowPaymentPlan = useMemo(() => {
    return paymentOptionType === 'PAY_DEPOSIT_WITH_PAYMENT_PLAN' && groups.length > 0;
  }, [ paymentOptionType, groups ]);

  const isContentEmpty = useMemo(() => {
    return (
      paymentOptionType !== 'PAY_LATER'
      && paymentOptionType !== 'PAY_CUSTOM_AMOUNT'
      && !isShowPaymentPlan
      && !subOptions.length
    );
  }, [ paymentOptionType, subOptions, isShowPaymentPlan ]);

  const payNowAmount = useMemo(() => {
    return <b>{currency({ amount, code: currencyCode, locale })}</b>;
  }, [ amount, currencyCode, locale ]);

  const payNextAmount = useMemo(() => {
    return <b>{currency({ amount: balance, code: currencyCode, locale })}</b>;
  }, [ balance, currencyCode, locale ]);

  const customAmountHint = useMemo(() => {
    return maxAmount && i18n('checkout.lib.components.text.notGreateThan', { 0: currency({ amount: maxAmount, code: currencyCode, locale }) });
  }, [ maxAmount, i18n, maxAmount, currencyCode, locale ]);

  useEffect(function updatePaymentOptionContainerClass() {
    const element = contentRef?.current;

    if (element) {
      if (!isContentEmpty) {
        element.classList.add('payment-option-container');
      } else if (element.classList.contains('payment-option-container')) {
        element.classList.remove('payment-option-container');
      }
    }
  }, [ contentRef, isContentEmpty ]);

  return <div className={`payment-option__content${paymentOptionType === 'PAY_DEPOSIT_WITH_PAYMENT_PLAN' ? ' pay-deposit-ml' : ''}`}>
    <div ref={contentRef}>
      {
        paymentOptionType === 'PAY_LATER'
      && <div className='pay-later'>
        {
          payLaterDescription
            ? interpolate(payLaterDescription, [ payNowAmount, payNextAmount ])
            : <I18nMessage id="checkout.lib.components.paymentOption.payNowPayLater" values={{ 0: payNowAmount, 1: payNextAmount }} />
        }
      </div>
      }
      {
        paymentOptionType === 'PAY_CUSTOM_AMOUNT'
        && <div className="pay-custom-amount">
          <FormGroup name="customAmount" required>
            <FormField
              as={CurrencyInput}
              maxlength="50"
              autoComplete="off"
              codePosition="prefix"
              code={currencyCode}
              hint={customAmountHint}
              onBlur={handleCustomAmountBlur}
              validate={customAmountValidate}/>
          </FormGroup>
        </div>
      }
      {
        subOptions.map(({ type: subType, creditBalance }, index) => {
          const totalCreditBalance = currencyCode && currency({ amount: creditBalance, code: currencyCode, locale });
          return (
            <React.Fragment key={index}>
              {
                subType === 'UPDATE_BILLING_SCHEDULE'
                && <FormGroup name="updateBillingSchedule">
                  <FormField as={CheckboxGroup} onChange={handleUpdateBillingScheduleChange}>
                    <Checkbox value="true">
                      <I18nMessage id="checkout.lib.components.text.applyToFutureBillings" />
                    </Checkbox>
                  </FormField>
                </FormGroup>
              }
              {
                subType === 'APPLY_CREDIT_BALANCE'
                && <FormGroup name="useCreditBalance">
                  <FormField as={CheckboxGroup} onChange={handleApplyCreditBalanceChange}>
                    <Checkbox value="true">
                      <I18nMessage id="checkout.lib.components.text.useCreditBalance" values={{ 0: <b>{totalCreditBalance}</b> }}></I18nMessage>
                    </Checkbox>
                  </FormField>
                </FormGroup>
              }
            </React.Fragment>
          );
        })
      }
      {
        isShowPaymentPlan
      && <PaymentOptionGroups groups={groups} currencyCode={currencyCode} locale={locale}/>
      }
    </div>
  </div>;
};

export default withI18n(PaymentOptionContent);
